const GOOGLE_TAG_MANAGER_CONTAINER_ID = /^GTM-[A-Z0-9]{4,20}$/;
const GOOGLE_ANALYTICS_4_MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;
const GOOGLE_ADS_CONVERSION_ID = /^AW-[0-9]{6,20}$/;
const GOOGLE_ADS_CONVERSION_LABEL = /^[A-Za-z0-9_-]{1,100}$/;

export interface GoogleTagManagerPublicConfig {
  readonly containerId: string;
}

export interface GoogleAnalytics4PublicConfig {
  readonly measurementId: string;
}

export interface GoogleAdsPublicConfig {
  readonly conversionId: string;
  readonly conversionLabel: string;
}

export function parseGoogleTagManagerPublicConfig(
  value: unknown,
): GoogleTagManagerPublicConfig | null {
  if (!hasExactKeys(value, ['containerId'])) {
    return null;
  }

  const containerId = normalizeIdentifier(value.containerId);

  if (!containerId || !GOOGLE_TAG_MANAGER_CONTAINER_ID.test(containerId)) {
    return null;
  }

  return Object.freeze({ containerId });
}

export function parseGoogleAnalytics4PublicConfig(
  value: unknown,
): GoogleAnalytics4PublicConfig | null {
  if (!hasExactKeys(value, ['measurementId'])) {
    return null;
  }

  const measurementId = normalizeIdentifier(value.measurementId);

  if (
    !measurementId ||
    !GOOGLE_ANALYTICS_4_MEASUREMENT_ID.test(measurementId)
  ) {
    return null;
  }

  return Object.freeze({ measurementId });
}

export function parseGoogleAdsPublicConfig(
  value: unknown,
): GoogleAdsPublicConfig | null {
  if (!hasExactKeys(value, ['conversionId', 'conversionLabel'])) {
    return null;
  }

  const conversionId = normalizeIdentifier(value.conversionId);
  const conversionLabel = normalizeIdentifier(value.conversionLabel);

  if (
    !conversionId ||
    !conversionLabel ||
    !GOOGLE_ADS_CONVERSION_ID.test(conversionId) ||
    !GOOGLE_ADS_CONVERSION_LABEL.test(conversionLabel)
  ) {
    return null;
  }

  return Object.freeze({ conversionId, conversionLabel });
}

function hasExactKeys<Key extends string>(
  value: unknown,
  keys: readonly Key[],
): value is Record<Key, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value) as unknown;

  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }

  const actualKeys = Object.keys(value);

  return (
    actualKeys.length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function normalizeIdentifier(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  return normalized.length === value.length ? normalized : null;
}
